import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { KIND_LABEL, DAY_LABEL, dayWords, progress, maltaToday } from '../../lib/people';
import { dayLabel } from '../../lib/when';
import { useFeature } from '../../lib/features';

const API = process.env.REACT_APP_API_URL;
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

/** Everybody an administrator can name: GET /users (active, or all). */
export function useStaff(enabled, { inactive = false } = {}) {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    axios.get(`${API}/users`, inactive ? { params: { include: 'inactive' } } : undefined)
      .then((r) => { if (live) setPeople(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setPeople([]); });
    return () => { live = false; };
  }, [enabled, inactive]);
  return people;
}

const nameOf = (u) => u.display_name || u.email;

/**
 * START A CHECKLIST by hand: joiner or leaver, who, their date, who sees it
 * through. A leaver may be anybody, switched off or not; a joiner only
 * somebody still active. Answers with the checklist - a new one, or the one
 * already open for them (the server says which).
 */
function StartForm({ onDone, onCancel }) {
  const navigate = useNavigate();
  const everyone = useStaff(true, { inactive: true });
  const [kind, setKind] = useState('joiner');
  const [userId, setUserId] = useState('');
  const [day, setDay] = useState(maltaToday());
  const [ownerId, setOwnerId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');

  const choices = useMemo(() => everyone
    .filter((u) => (kind === 'joiner' ? u.active !== false : true))
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b))), [everyone, kind]);
  const owners = useMemo(() => everyone.filter((u) => u.active !== false)
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b))), [everyone]);

  const submit = async (e) => {
    e.preventDefault();
    if (!userId) { setProblem('Choose the person.'); return; }
    setSaving(true);
    setProblem('');
    try {
      const r = await axios.post(`${API}/people/runs`, {
        kind, user_id: userId, day, note: note.trim() || null, ...(ownerId ? { owner_id: ownerId } : {})
      });
      onDone?.();
      navigate(`/people/runs/${r.data.run.id}`, { state: { created: r.data.created } });
    } catch (err) {
      setProblem(errorOf(err, 'Could not start the checklist.'));
    } finally { setSaving(false); }
  };

  return (
    <form className="pp-card pp-start" onSubmit={submit} aria-label="Start a checklist" noValidate>
      <h2 className="pp-card-title">Start a checklist</h2>
      <div className="pp-seg" role="radiogroup" aria-label="Kind">
        {['joiner', 'leaver'].map((k) => (
          <button key={k} type="button" role="radio" aria-checked={kind === k}
                  className={`pp-seg-btn${kind === k ? ' is-on' : ''}`} onClick={() => { setKind(k); setUserId(''); }}>
            {k === 'joiner' ? 'Somebody joining' : 'Somebody leaving'}
          </button>
        ))}
      </div>
      <div className="pp-grid">
        <label className="pp-field">
          <span>Who</span>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} required aria-label="Who">
            <option value="">Choose…</option>
            {choices.map((u) => (
              <option key={u.id} value={u.id}>{nameOf(u)}{u.active === false ? ' (deactivated)' : ''}</option>
            ))}
          </select>
        </label>
        <label className="pp-field">
          <span>{kind === 'joiner' ? 'Start date' : 'Leaving date'}</span>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} required />
        </label>
        <label className="pp-field">
          <span>Owner</span>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} aria-label="Owner">
            <option value="">Me</option>
            {owners.map((u) => <option key={u.id} value={u.id}>{nameOf(u)}</option>)}
          </select>
        </label>
        <label className="pp-field pp-wide">
          <span>Note (optional)</span>
          <input type="text" value={note} maxLength={4000} placeholder="e.g. replaces Marco in Audit"
                 onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
      <p className="pp-help">
        The steps come from the {kind} checklist in Settings, dated in working days from
        {kind === 'joiner' ? ' the start date' : ' the leaving date'}.
      </p>
      {problem && <p className="pp-problem" role="alert">{problem}</p>}
      <div className="pp-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Starting…' : 'Start checklist'}</button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/** One checklist in the list: who, when, how far along, what is late. */
function RunCard({ run, today }) {
  const p = progress(run);
  const overdue = Number(run.steps_overdue) || 0;
  const closed = run.status !== 'open';
  return (
    <li className="pp-run" data-run={run.id} data-kind={run.kind} data-status={run.status}>
      <Link to={`/people/runs/${run.id}`} className="pp-run-link">
        <span className={`pp-kind is-${run.kind}`}>{KIND_LABEL[run.kind]}</span>
        <span className="pp-run-main">
          <strong className="pp-run-name">{run.person_name}</strong>
          <span className="pp-run-meta">
            {DAY_LABEL[run.kind]} {run.day === today ? 'today' : dayWords(run.day)}
            {run.owner_name ? ` · ${run.owner_name}` : ''}
            {run.person_active === false && run.kind === 'leaver' ? ' · deactivated' : ''}
          </span>
        </span>
        <span className="pp-run-side">
          {closed ? (
            <span className={`badge ${run.status === 'done' ? 'badge-resolved' : 'badge-closed'}`}>
              {run.status === 'done' ? 'Done' : 'Cancelled'}
            </span>
          ) : overdue > 0 ? (
            <span className="pp-chip is-late">{overdue} overdue</span>
          ) : run.next_due ? (
            <span className="pp-chip">Next {dayLabel(run.next_due)}</span>
          ) : null}
          <span className="pp-bar" role="progressbar" aria-valuemin={0} aria-valuemax={p.total} aria-valuenow={p.done}
                aria-label={`${p.label} steps done`}>
            <span className="pp-bar-fill" style={{ width: `${Math.round(p.fraction * 100)}%` }} />
          </span>
          <span className="pp-run-count">{p.label}</span>
        </span>
      </Link>
    </li>
  );
}

export default function RunsList({ isAdmin }) {
  const [status, setStatus] = useState('open');
  const [kind, setKind] = useState('all');
  const [data, setData] = useState(null);
  const [problem, setProblem] = useState('');
  const [starting, setStarting] = useState(false);
  // Joiners & leavers switched off (wave 6): no new checklist; the open ones
  // are still here to be finished.
  const joinersOn = useFeature('joiners');

  const load = useCallback(() => {
    setProblem('');
    return axios.get(`${API}/people/runs`, { params: { status, ...(kind !== 'all' ? { kind } : {}) } })
      .then((r) => setData(r.data))
      .catch((e) => { setData({ runs: [], today: maltaToday() }); setProblem(errorOf(e, 'Could not load the checklists.')); });
  }, [status, kind]);
  useEffect(() => { load(); }, [load]);

  const runs = data?.runs || [];
  return (
    <section aria-label="Checklists" className="pp-section">
      <div className="pp-toolbar">
        <div className="pp-seg" role="radiogroup" aria-label="Show">
          {[['open', 'Open'], ['done', 'Closed']].map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={status === k}
                    className={`pp-seg-btn${status === k ? ' is-on' : ''}`} onClick={() => setStatus(k)}>{l}</button>
          ))}
        </div>
        <div className="pp-seg" role="radiogroup" aria-label="Kind">
          {[['all', 'Everybody'], ['joiner', 'Joiners'], ['leaver', 'Leavers']].map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k}
                    className={`pp-seg-btn${kind === k ? ' is-on' : ''}`} onClick={() => setKind(k)}>{l}</button>
          ))}
        </div>
        <span className="pp-spacer" />
        {isAdmin && joinersOn && !starting && (
          <button type="button" className="btn btn-primary" onClick={() => setStarting(true)}>Start a checklist</button>
        )}
      </div>
      {starting && joinersOn && <StartForm onDone={() => setStarting(false)} onCancel={() => setStarting(false)} />}
      {problem && <p className="pp-problem" role="alert">{problem}</p>}
      {data === null ? <p className="pp-quiet">Loading…</p> : runs.length === 0 ? (
        <div className="empty-state pp-empty">
          <p>
            {status === 'open'
              ? 'No checklists open. Adding somebody in Settings → Users starts a joiner checklist, and deactivating somebody starts a leaver one.'
              : 'Nothing closed yet.'}
          </p>
        </div>
      ) : (
        <ul className="pp-runs" aria-label={status === 'open' ? 'Open checklists' : 'Closed checklists'}>
          {runs.map((r) => <RunCard key={r.id} run={r} today={data.today} />)}
        </ul>
      )}
    </section>
  );
}
